import { motion, AnimatePresence } from "framer-motion"
import {X, Trash2, ShoppingBag, ArrowRight} from 'lucide-react'
import { Link } from "react-router-dom"
import { useCart } from "../contexts/CartContext"

const CartDrawer = () => {
  const { items, removeFromCart, clearCart, totalPrice, isDrawerOpen, closeDrawer } = useCart()

  const getFinalPrice = (price: number, discount?: number) => {
    if (discount && discount > 0) {
      return price * (1 - discount / 100)
    }
    return price
  }

  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            onClick={closeDrawer}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[61] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#D4C5B9]/30">
              <div className="flex items-center gap-3">
                <ShoppingBag size={20} className="text-[#8B7355]" />
                <h2 className="font-['Roboto'] text-xl font-semibold text-[#2C2C2C]">
                  Cart
                </h2>
                <span className="text-sm text-[#2C2C2C]/50">
                  ({items.length} {items.length > 1 ? "artworks" : "artwork"})
                </span>
              </div>
              <button
                onClick={closeDrawer}
                className="p-2 hover:bg-[#D4C5B9]/20 rounded-full transition-colors"
              >
                <X size={20} className="text-[#2C2C2C]" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <ShoppingBag size={48} className="text-[#D4C5B9] mb-4" />
                  <p className="text-lg font-['Roboto'] text-[#2C2C2C] mb-2">
                    Your cart is empty
                  </p>
                  <p className="text-sm text-[#2C2C2C]/50 mb-6">
                    Explore the gallery to discover our artworks
                  </p>
                  <Link
                    to="/gallery"
                    onClick={closeDrawer}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#8C6D3F] text-white rounded-lg hover:bg-[#6B4E2E] transition-colors"
                  >
                    View gallery
                    <ArrowRight size={16} />
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => (
                    <motion.div
                      key={item._id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: 100 }}
                      className="flex gap-4 p-3 bg-[#FAF9F6] rounded-lg"
                    >
                      {/* Thumbnail */}
                      <Link
                        to={`/artwork/${item._id}`}
                        onClick={closeDrawer}
                        className="w-20 h-24 flex-shrink-0 bg-white rounded overflow-hidden shadow-sm"
                      >
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          style={{ transform: `rotate(${item.rotation || 0}deg)` }}
                        />
                      </Link>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/artwork/${item._id}`}
                          onClick={closeDrawer}
                          className="font-['Roboto'] text-sm font-semibold text-[#7F1734] hover:text-[#5e1229] transition-colors line-clamp-1"
                        >
                          {item.title}
                        </Link>
                        <p className="text-xs text-[#2C2C2C]/50 mt-0.5">
                          {item.technique} - {item.dimensions}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          {item.discount && item.discount > 0 ? (
                            <>
                              <span className="text-sm font-semibold text-[#2C2C2C]">
                                {getFinalPrice(item.price, item.discount).toFixed(2)} €
                              </span>
                              <span className="text-xs text-[#2C2C2C]/40 line-through">
                                {item.price.toFixed(2)} €
                              </span>
                            </>
                          ) : (
                            <span className="text-sm font-semibold text-[#2C2C2C]">
                              {item.price.toFixed(2)} €
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() => removeFromCart(item._id)}
                        className="self-start p-1.5 text-[#2C2C2C]/30 hover:text-red-500 transition-colors"
                        title="Retirer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer with total and checkout */}
            {items.length > 0 && (
              <div className="border-t border-[#D4C5B9]/30 px-6 py-5 space-y-4">
                {/* Subtotal */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#2C2C2C]/60">Subtotal</span>
                  <span className="text-sm font-semibold text-[#2C2C2C]">
                    {totalPrice.toFixed(2)} €
                  </span>
                </div>
                <p className="text-xs text-[#2C2C2C]/40 flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="M7.5 14.5h2"/><path d="M2 10h20"/></svg>
                  Shipping calculated at checkout
                </p>

                {/* Checkout button */}
                <Link
                  to="/cart"
                  onClick={closeDrawer}
                  className="flex items-center justify-center gap-3 w-full px-8 py-4 bg-[#8C6D3F] text-white font-medium tracking-wider uppercase text-sm hover:bg-[#6B4E2E] transition-all duration-300 shadow-lg"
                >
                  <ShoppingBag size={18} />
                  Checkout
                </Link>

                {/* Clear cart */}
                <button
                  onClick={clearCart}
                  className="w-full text-center text-xs text-[#2C2C2C]/40 hover:text-red-500 transition-colors py-1"
                >
                  Clear cart
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default CartDrawer
